import { Injectable } from '@angular/core';
import { Notification, PaginatedNotificationListResponseList } from 'libs/mona/api/src/lib/model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiModels, HttpService } from '@mona/api';
import { Encounter, NotificationResolution } from '@mona/models';
import { isNullOrUndefined } from '@mona/shared/utils';
import { transformApiNotifications, transformApiNotificationsResponse } from './transforms';

/**
 * API abstraction layer for the notifications API
 */
@Injectable({
    providedIn: 'root',
})
export class NotificationsApi {
    apiBase = '/notifications/';

    /**
     * Constructor
     *
     * @param http HttpClient
     */
    constructor(private http: HttpService) {}

    /**
     * Get paginated notifications
     *
     * @param encounterId EntityId<Encounter>
     * @param url Optional URL for pagination (next/previous links)
     * @param page_size
     * @param readStatus
     */
    getPageableNotifications(
        encounterId?: EntityId<Encounter>,
        url?: string,
        page_size = 20,
        readStatus?: boolean,
    ): Observable<PaginatedNotificationListResponseList.Model> {
        const params: Record<string, string | number | boolean> = {};
        if (!url) {
            url = this.apiBase;
        }

        // Only add parameters if they're not already in the URL
        if (!url.includes('encounter_id=')) {
            params.encounter_id = encounterId;
        }
        if (!url.includes('page_size=')) {
            params.page_size = page_size;
        }
        if (!isNullOrUndefined(readStatus) && !url.includes('read_status=')) {
            params.read_status = readStatus;
        }

        return this.http
            .get<PaginatedNotificationListResponseList>(url, { params })
            .pipe(map(data => transformApiNotificationsResponse(data)));
    }

    /**
     * Get notification has-unread status
     *
     * @param encounterId EntityId<Encounter>
     */
    getUnreadNotificationStatus(encounterId?: EntityId<Encounter>): Observable<{ hasUnread: boolean }> {
        return this.http
            .get<{ has_unread: boolean }>(this.apiBase + 'has-unread', {
                params: {
                    encounter_id: `${encounterId}`,
                },
            })
            .pipe(
                map(hasUnreadResponse => ({
                    hasUnread: hasUnreadResponse.has_unread,
                })),
            );
    }

    /**
     * Marks multiple notifications as read
     *
     * @param notificationIds string[]
     */
    markMultipleNotificationsAsRead(notificationIds: string[]): Observable<Notification.Model[]> {
        return this.http
            .post<ApiModels.Notification[]>(`${this.apiBase}mark-all-read/`, { ids: notificationIds })
            .pipe(map(response => transformApiNotifications(response)));
    }

    /**
     * Resolves notification
     *
     * @param id EntityId<Notification.Model>
     * @param resolution NotificationResolution
     */
    resolveNotification(id: EntityId<Notification.Model>, resolution: NotificationResolution): Observable<void> {
        return this.http.post<void>(`${this.apiBase}/${id}/resolve/`, {
            resolution,
        });
    }
    /**
     * Changes the read_status of a notification
     *
     * @param id string
     * @param readStatus boolean
     */
    changeNotificationReadStatus(id: string, readStatus: boolean): Observable<Notification.Model> {
        return this.http
            .post<ApiModels.Notification>(`${this.apiBase}/${id}/change_read_status/`, {
                read_status: readStatus,
            })
            .pipe(map(notification => ApiModels.Notification.DTO.toModel(notification)));
    }
}
